import { useLocation } from "react-router-dom"

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { isLocale, type Locale } from "@/i18n/resolve"
import { useLocale, useT } from "@/i18n/provider"

const NAMES: Record<Locale, string> = { en: "English", ru: "Русский", uz: "Oʻzbekcha" }
const FLAGS: Record<Locale, string> = { en: "🇬🇧", ru: "🇷🇺", uz: "🇺🇿" }
const LANDING_PATHS = ["/", "/ru", "/uz"]

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { locale, setLocale } = useLocale()
  const location = useLocation()
  const t = useT()

  function onChange(value: string) {
    if (!isLocale(value)) return
    setLocale(value)
    if (LANDING_PATHS.includes(location.pathname)) {
      // Full load, not client-side navigation: /ru and /uz render under forced
      // LocaleProvider instances, so a client-side hop leaves the outer
      // provider stale. A fresh load re-detects locale from URL + storage and
      // serves the prerendered localized HTML.
      window.location.assign(value === "en" ? "/" : `/${value}`)
    }
  }

  return (
    <Select value={locale} onValueChange={onChange}>
      <SelectTrigger aria-label={t("nav.language")} className="h-8 w-auto gap-1.5 text-sm">
        <span aria-hidden="true">{FLAGS[locale]}</span>
        {/* Compact trigger (flag only) fits tight rows like the sidebar footer. */}
        <SelectValue>{compact ? null : NAMES[locale]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {(Object.keys(NAMES) as Locale[]).map((code) => (
          <SelectItem key={code} value={code}>
            <span aria-hidden="true">{FLAGS[code]}</span> {NAMES[code]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
